"use client";

import { c as _c } from "react/compiler-runtime";
import { IconFullscreen1 } from "central-icons/IconFullscreen1";
import { IconPause } from "central-icons/IconPause";
import { IconPlay } from "central-icons/IconPlay";
import { useReducedMotion } from "motion/react";
import { useEffect, useRef } from "react";
import VideoScrubber from "@/components/mdx/video-scrubber";
import VideoSpeedControl from "@/components/mdx/video-speed-control";
import { AnimatedIcon } from "@/components/ui/animated-icon";
import { IconButton } from "@/components/ui/icon-button";
import { useTimedState } from "@/hooks/use-timed-state";
import { formatDuration } from "@/lib/format-duration";
import { cn } from "@/lib/utils";
import { useVideoElementState } from "./use-video-element-state";
interface VideoPlayerProps extends Omit<React.VideoHTMLAttributes<HTMLVideoElement>, "src" | "width" | "height" | "className"> {
  aspectRatio?: string;
  bg?: string;
  className?: string;
  height?: number | string;
  playbackRate?: number;
  src: string;
  width?: number | string;
}
export default function VideoPlayer(t0) {
  const $ = _c(79);
  let aspectRatio;
  let bg;
  let className;
  let height;
  let playbackRate;
  let rest;
  let src;
  let width;
  if ($[0] !== t0) {
    ({
      src,
      bg,
      className,
      aspectRatio,
      width,
      height,
      playbackRate,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = aspectRatio;
    $[2] = bg;
    $[3] = className;
    $[4] = height;
    $[5] = playbackRate;
    $[6] = rest;
    $[7] = src;
    $[8] = width;
  } else {
    aspectRatio = $[1];
    bg = $[2];
    className = $[3];
    height = $[4];
    playbackRate = $[5];
    rest = $[6];
    src = $[7];
    width = $[8];
  }
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  const {
    isPlaying,
    currentTime,
    duration,
    rate,
    seek,
    setRate,
    toggle
  } = useVideoElementState(videoRef, playbackRate);
  const [showIndicator, flashIndicator, hideIndicator] = useTimedState(false, 1000);
  let t1;
  let t2;
  if ($[9] !== prefersReducedMotion) {
    t1 = () => {
      const video = videoRef.current;
      if (!(video && prefersReducedMotion)) {
        return;
      }
      video.pause();
    };
    t2 = [prefersReducedMotion];
    $[9] = prefersReducedMotion;
    $[10] = t1;
    $[11] = t2;
  } else {
    t1 = $[10];
    t2 = $[11];
  }
  useEffect(t1, t2);
  let t3;
  if ($[12] !== flashIndicator || $[13] !== hideIndicator || $[14] !== isPlaying || $[15] !== toggle) {
    t3 = () => {
      const video_0 = videoRef.current;
      const wasPlaying = video_0 ? !video_0.paused : isPlaying;
      toggle();
      if (wasPlaying) {
        flashIndicator(true);
      } else {
        hideIndicator();
      }
    };
    $[12] = flashIndicator;
    $[13] = hideIndicator;
    $[14] = isPlaying;
    $[15] = toggle;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  const handleToggle = t3;
  let t4;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const container = containerRef.current;
      if (!container) {
        return;
      }
      const swallow = _temp;
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(swallow);
      } else {
        container.requestFullscreen().catch(swallow);
      }
    };
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const toggleFullscreen = t4;
  const remaining = Math.max(0, duration - currentTime);
  const computedAspect = aspectRatio ?? (width && height ? `${width} / ${height}` : undefined);
  let t5;
  if ($[18] !== className) {
    t5 = cn("group relative w-full select-none overflow-hidden rounded-xl", className);
    $[18] = className;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== bg || $[21] !== computedAspect) {
    t6 = {
      aspectRatio: computedAspect,
      backgroundColor: bg
    };
    $[20] = bg;
    $[21] = computedAspect;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const t7 = !prefersReducedMotion;
  let t8;
  if ($[23] !== rest || $[24] !== src || $[25] !== t7) {
    t8 = <video autoPlay={t7} loop={true} muted={true} playsInline={true} preload="metadata" {...rest} className="absolute inset-0 h-full w-full object-contain" ref={videoRef} src={src} />;
    $[23] = rest;
    $[24] = src;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-xl border-overlay" />;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  const t10 = isPlaying ? "Pause video" : "Play video";
  let t11;
  if ($[28] !== handleToggle || $[29] !== t10) {
    t11 = <button aria-label={t10} className="absolute inset-0 bottom-11 z-0 cursor-pointer rounded-xl focus-visible:outline-2 focus-visible:outline-white focus-visible:-outline-offset-4 [@media(hover:none)]:bottom-0" onClick={handleToggle} type="button" />;
    $[28] = handleToggle;
    $[29] = t10;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const t12 = showIndicator && "opacity-100";
  const t13 = !isPlaying && "opacity-100";
  let t14;
  if ($[31] !== t12 || $[32] !== t13) {
    t14 = cn("grid size-16 place-items-center rounded-full bg-black/25 text-white/90 opacity-0 backdrop-blur-sm transition-opacity duration-200 ease-out will-change-transform group-hover:opacity-100", t12, t13);
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  let t16;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <IconPause className="size-7 will-change-transform" />;
    t16 = <IconPlay className="mr-px size-7 will-change-transform" />;
    $[34] = t15;
    $[35] = t16;
  } else {
    t15 = $[34];
    t16 = $[35];
  }
  let t17;
  if ($[36] !== isPlaying) {
    t17 = <AnimatedIcon active={isPlaying} activeIcon={t15} idleIcon={t16} />;
    $[36] = isPlaying;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t14 || $[39] !== t17) {
    t18 = <div className="pointer-events-none absolute inset-0 grid place-items-center"><div className={t14}>{t17}</div></div>;
    $[38] = t14;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="gradient-ease-out pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-linear-to-t from-black/70 to-transparent opacity-0 transition-opacity duration-200 ease-out group-hover:opacity-100 group-has-focus-visible:opacity-100" />;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  const t20 = isPlaying ? "Pause video" : "Play video";
  let t21;
  let t22;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <IconPause className="size-4 will-change-transform" />;
    t22 = <IconPlay className="mr-px size-4 will-change-transform" />;
    $[42] = t21;
    $[43] = t22;
  } else {
    t21 = $[42];
    t22 = $[43];
  }
  let t23;
  if ($[44] !== isPlaying) {
    t23 = <AnimatedIcon active={isPlaying} activeIcon={t21} idleIcon={t22} />;
    $[44] = isPlaying;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] !== handleToggle || $[47] !== t20 || $[48] !== t23) {
    t24 = <IconButton aria-label={t20} onClick={handleToggle} size="2xs" type="button" variant="overlay">{t23}</IconButton>;
    $[46] = handleToggle;
    $[47] = t20;
    $[48] = t23;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== currentTime) {
    t25 = formatDuration(currentTime);
    $[50] = currentTime;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== t25) {
    t26 = <span className="shrink-0 font-[450] text-white text-xs tabular-nums">{t25}</span>;
    $[52] = t25;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] !== currentTime || $[55] !== duration || $[56] !== seek) {
    t27 = <VideoScrubber max={duration} onSeek={seek} value={currentTime} />;
    $[54] = currentTime;
    $[55] = duration;
    $[56] = seek;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] !== remaining) {
    t28 = formatDuration(remaining);
    $[58] = remaining;
    $[59] = t28;
  } else {
    t28 = $[59];
  }
  let t29;
  if ($[60] !== t28) {
    t29 = <span className="shrink-0 font-[450] text-white text-xs tabular-nums">{t28}</span>;
    $[60] = t28;
    $[61] = t29;
  } else {
    t29 = $[61];
  }
  let t30;
  if ($[62] !== rate || $[63] !== setRate) {
    t30 = <VideoSpeedControl onChange={setRate} value={rate} />;
    $[62] = rate;
    $[63] = setRate;
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  let t31;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <IconButton aria-label="Toggle fullscreen" onClick={toggleFullscreen} size="2xs" type="button" variant="overlay"><IconFullscreen1 className="size-4" /></IconButton>;
    $[65] = t31;
  } else {
    t31 = $[65];
  }
  let t32;
  if ($[66] !== t24 || $[67] !== t26 || $[68] !== t27 || $[69] !== t29 || $[70] !== t30) {
    t32 = <div className="absolute inset-x-2 bottom-2 z-10 flex h-9 translate-y-2 items-center gap-2 px-2 opacity-0 transition duration-200 ease-out group-hover:translate-y-0 group-hover:opacity-100 group-has-focus-visible:translate-y-0 group-has-focus-visible:opacity-100">{t24}{t26}{t27}{t29}{t30}{t31}</div>;
    $[66] = t24;
    $[67] = t26;
    $[68] = t27;
    $[69] = t29;
    $[70] = t30;
    $[71] = t32;
  } else {
    t32 = $[71];
  }
  let t33;
  if ($[72] !== t11 || $[73] !== t18 || $[74] !== t32 || $[75] !== t5 || $[76] !== t6 || $[77] !== t8) {
    t33 = <div className={t5} ref={containerRef} style={t6}>{t8}{t9}{t11}{t18}{t19}{t32}</div>;
    $[72] = t11;
    $[73] = t18;
    $[74] = t32;
    $[75] = t5;
    $[76] = t6;
    $[77] = t8;
    $[78] = t33;
  } else {
    t33 = $[78];
  }
  return t33;
}
function _temp() {}